/**
 * @file useToast.js
 * @location src/store/useToast.js
 * @role 토스트 알림 훅 — 큐 기반 (최대 3개 누적)
 *
 * 사용법: const { toasts, showToast, dismissToast } = useToast();
 *   showToast('저장했습니다')                            기본 (ok, 2200ms)
 *   showToast('실패했습니다', 'err')                     에러
 *   showToast('삭제했습니다', 'err', {                   액션 버튼
 *     duration: 5000,
 *     action: { label: '되돌리기', onClick: () => act('UNDO') }
 *   })
 *
 * 길이 가변: 글자수 기반으로 표시 시간 자동 조정 (1.8s ~ 4.5s)
 */
import { useState, useCallback, useRef } from 'react';

const MAX_QUEUE = 3;

const autoDuration = (msg) => {
  const len = String(msg ?? '').length;
  return Math.max(1800, Math.min(4500, 1200 + len * 70));
};

export function useToast() {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismissToast = useCallback((id) => {
    setToasts(list => list.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((msg, type = 'ok', opts = {}) => {
    const id = ++idRef.current;
    const duration = opts.duration ?? (opts.action ? 5000 : autoDuration(msg));
    const action = opts.action; // { label, onClick }
    setToasts(list => {
      const next = [...list, { id, msg, type, action }];
      return next.length > MAX_QUEUE ? next.slice(next.length - MAX_QUEUE) : next;
    });
    setTimeout(() => {
      setToasts(list => list.filter(t => t.id !== id));
    }, duration);
    return id;
  }, []);

  return { toasts, showToast, dismissToast };
}
